'use client';

import React from 'react';
import { styled } from '@linaria/react';
import { animated, useSpring } from 'react-spring';

import { SUPERHEADER_HEIGHT } from '@/constants';
import useHasHydrated from '@/hooks/use-has-hydrated';
import useBoopMinimal from '@/hooks/use-boop-minimal';

import VisuallyHidden from '@/components/VisuallyHidden';
import MaxWidthWrapper from '@/components/MaxWidthWrapper';

import {
  useGlobalUIState,
  useGlobalUIActions,
} from '@/components/GlobalUIProvider';
import usePrefersReducedMotion from '@/hooks/use-prefers-reduced-motion';

type CloseStatus = 'idle' | 'hover' | 'active';

export interface Props extends React.HTMLAttributes<HTMLDivElement> {
  superheaderId: string;
  maxWidth?: number | string;
  children?: React.ReactNode;
}

function Superheader({
  superheaderId,
  maxWidth,
  children,
  ...delegated
}: Props) {
  const { superheader } = useGlobalUIState();
  const { dismissSuperheader } = useGlobalUIActions();

  const id = React.useId();
  const patternId = id.replace(/:/g, '') + '-pattern';

  const prefersReducedMotion = usePrefersReducedMotion();
  const hasHydrated = useHasHydrated();

  const [closeStatus, setCloseStatus] =
    React.useState<CloseStatus>('idle');
  const isCloseBooped = useBoopMinimal(closeStatus === 'hover');

  const path = useSpring({
    d:
      closeStatus === 'active'
        ? `M 18,12 L 6,12 M 6,12 L 18,12`
        : isCloseBooped
          ? `M 18,8 L 6,16 M 6,8 L 18,16`
          : `M 18,6 L 6,18 M 6,6 L 18,18`,
    config: {
      tension: 300,
      friction: closeStatus === 'idle' ? 40 : 10,
    },
    immediate: prefersReducedMotion,
  });

  React.useEffect(() => {
    function handleKeyDown(ev: KeyboardEvent) {
      if (ev.key === 'Escape') {
        dismissSuperheader();
      }
    }

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [dismissSuperheader]);

  const isDismissed = superheader !== superheaderId;

  if (isDismissed) {
    return null;
  }

  return (
    <Wrapper {...delegated}>
      <Zigzag>
        <svg width="100%" height="15">
          <defs>
            <pattern
              id={patternId}
              x="0"
              y="0"
              width="90"
              height="30"
              patternUnits="userSpaceOnUse"
              patternTransform="scale(0.5)"
            >
              <path
                d="M149.176 15.0712C149.705 14.5137 150 13.7744 150 13.0057V-16.5C150 -18.1569 148.657 -19.5 147 -19.5H-57C-58.6569 -19.5 -60 -18.1569 -60 -16.5V13.0057C-60 13.7744 -59.7049 14.5137 -59.1757 15.0712C-55.5419 18.8988 -50.68 22.5 -45 22.5C-37.5355 22.5 -32.6678 18.3758 -29.202 14.8544C-28.4811 14.122 -27.8615 13.4623 -27.2957 12.8599C-26.3923 11.8981 -25.6259 11.0822 -24.8032 10.3488C-23.5088 9.19499 -22.8454 9 -22.5 9C-22.1546 9 -21.4912 9.19499 -20.1968 10.3488C-19.3741 11.0822 -18.6077 11.8981 -17.7044 12.8599C-17.1385 13.4623 -16.5189 14.1219 -15.798 14.8544C-12.3322 18.3758 -7.46449 22.5 0 22.5C7.46449 22.5 12.3322 18.3758 15.798 14.8544C16.5189 14.122 17.1385 13.4623 17.7043 12.8599C18.6077 11.8981 19.3741 11.0822 20.1968 10.3488C21.4912 9.19499 22.1546 9 22.5 9C22.8454 9 23.5088 9.19499 24.8032 10.3488C25.6259 11.0822 26.3923 11.8981 27.2957 12.8599C27.8615 13.4623 28.4811 14.122 29.202 14.8544C32.6678 18.3758 37.5355 22.5 45 22.5C52.4645 22.5 57.3322 18.3758 60.798 14.8544C61.5189 14.122 62.1385 13.4623 62.7043 12.8599C63.6077 11.8981 64.3741 11.0822 65.1968 10.3488C66.4912 9.19499 67.1546 9 67.5 9C67.8454 9 68.5088 9.19499 69.8032 10.3488C70.6259 11.0822 71.3923 11.8981 72.2957 12.8599C72.8615 13.4623 73.4811 14.122 74.202 14.8544C77.6678 18.3758 82.5355 22.5 90 22.5C97.4645 22.5 102.332 18.3758 105.798 14.8544C106.519 14.122 107.138 13.4623 107.704 12.8599C108.608 11.8981 109.374 11.0822 110.197 10.3488C111.491 9.19499 112.155 9 112.5 9C112.845 9 113.509 9.19499 114.803 10.3488C115.626 11.0822 116.392 11.8981 117.296 12.8599C117.862 13.4623 118.481 14.122 119.202 14.8544C122.668 18.3758 127.536 22.5 135 22.5C140.68 22.5 145.542 18.8988 149.176 15.0712Z"
                fill="var(--superheader-bg)"
                stroke="var(--superheader-stroke)"
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </pattern>
          </defs>
          <rect
            width="100%"
            height="100%"
            fill={`url(#${patternId})`}
          />
        </svg>
      </Zigzag>
      <Row maxWidth={maxWidth}>
        <Side />

        <p>{children}</p>
        <Side>
          {hasHydrated && (
            <CloseBtn
              onClick={(ev) => {
                ev.stopPropagation();
                dismissSuperheader();
              }}
              onMouseEnter={() => setCloseStatus('hover')}
              onMouseLeave={() => setCloseStatus('idle')}
              onMouseDown={() => setCloseStatus('active')}
              onMouseUp={() => setCloseStatus('idle')}
            >
              <XIcon
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <animated.path d={path.d} />
              </XIcon>
              <VisuallyHidden>Dismiss banner</VisuallyHidden>
            </CloseBtn>
          )}
        </Side>
      </Row>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  --superheader-bg: hsl(45deg 100% 75%);
  --superheader-stroke: hsl(200deg 20% 8%);
  --selection-background-color: hsl(45deg 100% 88%);
  --selection-text-color: black;
  --color-primary: hsl(35deg 80% 30%);
  position: relative;
  display: flex;
  align-items: center;
  height: ${SUPERHEADER_HEIGHT}rem;
  font-size: 0.9375rem;
  color: black;
  /* Ensure the zigzag doesn't cover the header */
  margin-bottom: 10px;
  animation: slideIn 1200ms both cubic-bezier(0.247, 1.038, 0, 1);
  animation-delay: 100ms;
  background: var(--superheader-bg);

  @keyframes slideIn {
    from {
      transform: translateY(calc(-${SUPERHEADER_HEIGHT}rem - 16px));
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Row = styled(MaxWidthWrapper)`
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 34rem) {
    justify-content: center;
  }
`;

const Side = styled.div`
  position: relative;
  width: 32px;
  height: ${SUPERHEADER_HEIGHT}rem;
  display: flex;
  justify-content: center;
  align-items: center;
  aspect-ratio: 1 / 1;

  @media (max-width: 34rem) {
    display: none;
  }
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 0;
  left: -8px;
  right: -8px;
  bottom: 0;
  display: grid;
  place-content: center;
  animation: fadeFromTransparent 500ms;
  will-change: transform;

  @keyframes enterCloseButton {
    from {
      opacity: 0;
      transform: translateY(-4px);
    }
  }

  @media (prefers-reduced-motion: no-preference) {
    animation: enterCloseButton 600ms
      cubic-bezier(0.393, 0.873, 0, 1.007);
  }
`;

const Zigzag = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 15px;
  /* Slide up by 1px to avoid a subpixel gap when animating in */
  transform: translateY(calc(100% - 1px));
  /* overflow: hidden; */
`;

const XIcon = styled.svg`
  width: 24px;
  height: 24px;
  fill: none;
  stroke: currentColor;
  stroke-width: 2px;
  stroke-linecap: round;
  stroke-linejoin: round;
`;

export default Superheader;
